import { ff } from '@atlassian/jira-feature-flagging';
import { DEFAULT_ITEM_TYPE } from '../../common/constants';
import type { ThirdPartyFieldId, FieldId, ThirdPartyField } from '../../common/constants/fields';
import type {
	MappedJiraFields,
	ThirdPartyFieldConfigState,
	ThirdPartyFieldMapping,
	ThirdPartyFieldMappingsState,
} from '../../common/types';
import type { ThirdPartyFieldConfigData } from './types';

export const transformToThirdPartyFieldMappingsState = (
	thirdPartyFieldMappings: ThirdPartyFieldMapping[],
): ThirdPartyFieldMappingsState => {
	const fieldMappingsState: ThirdPartyFieldMappingsState = {};

	thirdPartyFieldMappings.forEach((fieldMapping) => {
		fieldMappingsState[fieldMapping.thirdPartyField.fieldId] = {
			...fieldMapping,
			defaultMappedJiraField: fieldMapping.mappedJiraField,
		};
	});

	return fieldMappingsState;
};

export const transformToThirdPartyFieldOrder = (
	thirdPartyFieldMappings: ThirdPartyFieldMapping[],
	filteredFieldId?: string,
): ThirdPartyFieldId[] =>
	thirdPartyFieldMappings
		// filter out hierarchy mapping field
		.filter(
			(fieldMapping) =>
				!ff('jwm-x2c-hierarchy-mapping_kkjnd') ||
				fieldMapping.thirdPartyField.fieldId !== filteredFieldId,
		)
		.map((fieldMapping) => fieldMapping.thirdPartyField.fieldId);

export const transformToMappedJiraFields = (
	thirdPartyFieldMappings: ThirdPartyFieldMapping[],
): MappedJiraFields => {
	const mappedJiraFields: MappedJiraFields = {};

	thirdPartyFieldMappings
		.filter((fieldMapping) => !!fieldMapping.mappedJiraField.fieldId)
		.forEach((fieldMapping) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			mappedJiraFields[fieldMapping.mappedJiraField.fieldId as FieldId] =
				fieldMapping.thirdPartyField.fieldId;
		});

	return mappedJiraFields;
};

export const transformToIncludedJiraFields = (
	thirdPartyFieldMappings: ThirdPartyFieldMapping[],
): Set<FieldId> => {
	const includedJiraFieldIds: Set<FieldId> = new Set();

	thirdPartyFieldMappings
		.filter((fieldMapping) => !!fieldMapping.mappedJiraField.fieldId)
		.forEach((fieldMapping) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			includedJiraFieldIds.add(fieldMapping.mappedJiraField.fieldId as FieldId);
		});

	return includedJiraFieldIds;
};

const transformToThirdPartyItemTypeFieldMapping = (
	thirdPartyFieldMappings: ThirdPartyFieldMapping<ThirdPartyField>[],
	filteredFieldId?: string,
) => ({
	mappedJiraFields: transformToMappedJiraFields(thirdPartyFieldMappings),
	thirdPartyFieldMappings: transformToThirdPartyFieldMappingsState(thirdPartyFieldMappings),
	thirdPartyFieldOrder: transformToThirdPartyFieldOrder(thirdPartyFieldMappings, filteredFieldId),
	includedJiraFieldIds: transformToIncludedJiraFields(thirdPartyFieldMappings),
});

export const transformToThirdPartyFieldConfigState = ({
	thirdPartyFieldMappings,
	availableJiraFields,
	itemTypes,
	hierarchyMapping,
	hasUnsupportedFieldFiltered,
}: ThirdPartyFieldConfigData): ThirdPartyFieldConfigState => {
	if (!itemTypes || itemTypes.length === 0) {
		return {
			availableJiraFields,
			fieldMappingState: {
				[DEFAULT_ITEM_TYPE]: transformToThirdPartyItemTypeFieldMapping(
					thirdPartyFieldMappings,
					hierarchyMapping?.mappingFieldId,
				),
			},
			hierarchyMapping,
			hasUnsupportedFieldFiltered,
		};
	}

	let fieldMappingState = {};
	itemTypes.forEach(({ itemTypeKey, itemTypeFieldMappings, itemTypeId }) => {
		fieldMappingState = {
			...fieldMappingState,
			[itemTypeKey]: {
				...transformToThirdPartyItemTypeFieldMapping(
					itemTypeFieldMappings,
					hierarchyMapping?.itemType === itemTypeKey ? hierarchyMapping?.mappingFieldId : undefined,
				),
				itemTypeId,
			},
		};
	});
	return {
		availableJiraFields,
		fieldMappingState,
		hierarchyMapping,
		hasUnsupportedFieldFiltered,
	};
};
